import React, { useState } from 'react';
import { CheckCircle2Icon, CircleIcon, ExternalLinkIcon, CopyIcon, Share2Icon, RocketIcon, EyeIcon } from 'lucide-react';
import { Card, StepHeading } from '../SettingsUI';
import { ShopConfig } from '../../../data/shopConfig';
import { StorefrontViewport } from '../StorefrontViewport';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
  onOpenPreview: () => void;
}
export function ReviewPublish({
  config,
  update,
  onOpenPreview
}: Props) {
  const [copied, setCopied] = useState(false);
  const url = `talab.app/shop/${config.handle}`;
  const checks = [{
    label: 'Business information',
    done: Boolean(config.publicName && config.about)
  }, {
    label: 'Verification',
    done: config.verification.identity === 'verified'
  }, {
    label: 'Contact information',
    done: Boolean(config.contact.email || config.contact.phone)
  }, {
    label: 'Branding',
    done: Boolean(config.logo && config.theme.primary)
  }, {
    label: 'AI assistant',
    done: Boolean(config.ai.greeting)
  }, {
    label: 'Catalogue connected',
    done: config.content.categories.length > 0
  }];
  const copyLink = () => {
    navigator.clipboard?.writeText(`https://${url}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return <div className="space-y-5" data-id="element-4002" data-mp="1a05tajt7os8r">
      <StepHeading title={config.published ? 'Your shop is live' : 'Your shop is ready 🎉'} desc={config.published ? 'Customers can now find and message your shop. You can keep editing anything at any time.' : 'Have a last look, then publish whenever you are ready. Nothing is public until you press publish.'} data-id="element-4003" data-mp="19z47bkanqlc9" />

      <Card data-id="element-4004" data-mp="3lqrp1suqzymy">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-id="element-4005" data-mp="3lr15aopzqrfg">
          {checks.map(c => <div key={c.label} className="flex items-center gap-2.5 py-2" data-id="element-4006" data-mp="3prjxijvspzi6">
              {c.done ? <CheckCircle2Icon className="w-4 h-4 text-success flex-shrink-0" data-id="element-4007" data-mp="wwtjbxq2xlhw" /> : <CircleIcon className="w-4 h-4 text-slate-300 flex-shrink-0" data-id="element-4008" data-mp="rzb63v514k43" />}
              <span className={`text-sm ${c.done ? 'text-slate-700' : 'text-slate-400'}`} data-id="element-4009" data-mp="15ysy0jdmgdho">
                {c.label}
              </span>
            </div>)}
        </div>
      </Card>

      <Card className="!p-0 overflow-hidden" data-id="element-4010" data-mp="1ft2v2edidzyq">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100" data-id="element-4011" data-mp="1avb3jlgs0fv2">
          <p className="text-sm font-semibold text-dark-navy" data-id="element-4012" data-mp="1pk78varg7erz">
            Your shop preview
          </p>
          <button onClick={onOpenPreview} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-indigo hover:underline" data-id="element-4013" data-mp="1kngrmk3wqd4z">
            Open full preview
            <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-4014" data-mp="1tof0pgt7dauf" />
          </button>
        </div>
        <StorefrontViewport config={config} device="desktop" zoom="fit" className="h-[460px] bg-slate-50" data-id="element-4015" data-mp="2d9xa713j8ww8" />
      </Card>

      <Card data-id="element-4016" data-mp="248h2j143r8vp">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2" data-id="element-4017" data-mp="246w5n4ubfduo">
          Your public shop address
        </p>
        <div className="flex flex-wrap items-center gap-3" data-id="element-4018" data-mp="287px286yvasb">
          <p className="font-display font-bold text-lg text-dark-navy" data-id="element-4019" data-mp="1uh37b5j6ov8d">{url}</p>
          <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-600 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-4020" data-mp="1z9ctcah6hb5d">
            <CopyIcon className="w-3.5 h-3.5" data-id="element-4021" data-mp="1bj0c2901m224" />
            {copied ? 'Copied' : 'Copy Link'}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mt-5" data-id="element-4022" data-mp="1ghtsa96ps75z">
          <button onClick={onOpenPreview} className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-4023" data-mp="18hdwjirsledo">
            <EyeIcon className="w-4 h-4" data-id="element-4024" data-mp="uq8wifkiik2t" />
            Preview Shop
          </button>
          {config.published ? <>
              <a href={`/shop/${config.handle}`} className="inline-flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4025" data-mp="ps7cnsf9igqa">
                <ExternalLinkIcon className="w-4 h-4" data-id="element-4026" data-mp="bro8iwdx3ute" />
                View Shop
              </a>
              <button className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-4027" data-mp="2a6r5furnentv">
                <Share2Icon className="w-4 h-4" data-id="element-4028" data-mp="3o1zm9y90daaf" />
                Share
              </button>
            </> : <button onClick={() => update({
          published: true
        })} className="inline-flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4029" data-mp="3t0au63fqtaq0">
              <RocketIcon className="w-4 h-4" data-id="element-4030" data-mp="3a2zz6v1593gd" />
              Publish Shop
            </button>}
        </div>
      </Card>
    </div>;
}